/**
 * State Management
 * Simple reactive state store for the quiz player
 */

import { createLogger } from './logger';
import {
  AppState,
  FunnelConfig,
  Answer,
  GDPRConsent,
  UTMParams,
  OperatingMode
} from '../types';

const logger = createLogger('State');

// Generate unique session ID
function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

// Generate unique lead ID
export function generateLeadId(): string {
  return `lead_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

// Initial state
function createInitialState(): AppState {
  return {
    operatingMode: 'production',
    funnel: null,
    funnelStatus: null,
    loadError: null,

    quiz: {
      currentQuestionIndex: 0,
      answers: {},
      isComplete: false,
      startedAt: null,
      completedAt: null
    },

    smartPaths: {
      activePathId: null,
      visitedPaths: [],
      completedPaths: []
    },

    form: {
      fields: {},
      errors: {},
      isSubmitting: false,
      isSubmitted: false,
      leadId: null
    },

    compliance: {
      ageVerified: false,
      ageVerifiedAt: null,
      gdprConsent: null,
      gdprConsentAt: null
    },

    session: {
      sessionId: generateSessionId(),
      leadId: null,
      utmParams: {},
      customParams: {},
      referrer: document.referrer || null,
      userAgent: navigator.userAgent
    },

    ui: {
      isLoading: true,
      showAgeGate: false,
      showGDPRModal: false,
      showModeBanner: false,
      showSmartPath: false
    }
  };
}

// The state singleton
let state: AppState = createInitialState();

// Subscribers for state changes
type StateSubscriber = (state: AppState) => void;
const subscribers: Set<StateSubscriber> = new Set();

/**
 * Notify all subscribers of state change
 */
function notifySubscribers(): void {
  subscribers.forEach(subscriber => {
    try {
      subscriber(state);
    } catch (error) {
      logger.error('Subscriber error', error);
    }
  });
}

/**
 * Subscribe to state changes
 */
export function subscribe(callback: StateSubscriber): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Get current state (readonly)
 */
export function getState(): Readonly<AppState> {
  return state;
}

/**
 * Reset state to initial
 */
export function resetState(): void {
  state = createInitialState();
  logger.info('State reset');
  notifySubscribers();
}

// ============================================
// Operating Mode
// ============================================

export function setOperatingMode(mode: OperatingMode): void {
  state.operatingMode = mode;
  logger.debug('Operating mode set', { mode });
  notifySubscribers();
}

// ============================================
// Funnel State
// ============================================

export function setFunnel(funnel: FunnelConfig): void {
  state.funnel = funnel;
  state.funnelStatus = funnel.status;
  state.loadError = null;
  logger.info('Funnel loaded', { id: funnel.funnelID, version: funnel.version });
  notifySubscribers();
}

export function setFunnelError(error: string): void {
  state.loadError = error;
  logger.error('Funnel load error', { error });
  notifySubscribers();
}

// ============================================
// Quiz State
// ============================================

export function startQuiz(): void {
  state.quiz.startedAt = Date.now();
  logger.info('Quiz started');
  notifySubscribers();
}

export function setCurrentQuestion(index: number): void {
  state.quiz.currentQuestionIndex = index;
  logger.debug('Current question', { index });
  notifySubscribers();
}

export function recordAnswer(answer: Answer): void {
  state.quiz.answers[answer.questionId] = answer;
  logger.debug('Answer recorded', {
    questionId: answer.questionId,
    optionIndex: answer.optionIndex
  });
  notifySubscribers();
}

export function completeQuiz(): void {
  state.quiz.isComplete = true;
  state.quiz.completedAt = Date.now();
  logger.info('Quiz completed', {
    duration: state.quiz.completedAt - (state.quiz.startedAt || 0),
    answerCount: Object.keys(state.quiz.answers).length
  });
  notifySubscribers();
}

export function getAnswers(): Record<string, Answer> {
  return { ...state.quiz.answers };
}

// ============================================
// Smart Paths State
// ============================================

export function showSmartPath(pathId: string): void {
  state.smartPaths.activePathId = pathId;
  state.ui.showSmartPath = true;
  if (!state.smartPaths.visitedPaths.includes(pathId)) {
    state.smartPaths.visitedPaths.push(pathId);
  }
  logger.info('Smart path shown', { pathId });
  notifySubscribers();
}

export function closeSmartPath(): void {
  state.smartPaths.activePathId = null;
  state.ui.showSmartPath = false;
  notifySubscribers();
}

export function completeSmartPath(pathId: string): void {
  if (!state.smartPaths.completedPaths.includes(pathId)) {
    state.smartPaths.completedPaths.push(pathId);
  }
  logger.info('Smart path completed', { pathId });
  notifySubscribers();
}

// ============================================
// Form State
// ============================================

export function setFormField(name: string, value: string): void {
  state.form.fields[name] = value;
  // Clear error when field is updated
  if (state.form.errors[name]) {
    delete state.form.errors[name];
  }
  notifySubscribers();
}

export function setFormError(name: string, error: string): void {
  state.form.errors[name] = error;
  notifySubscribers();
}

export function clearFormErrors(): void {
  state.form.errors = {};
  notifySubscribers();
}

export function setFormSubmitting(isSubmitting: boolean): void {
  state.form.isSubmitting = isSubmitting;
  notifySubscribers();
}

export function setFormSubmitted(leadId: string): void {
  state.form.isSubmitted = true;
  state.form.leadId = leadId;
  state.session.leadId = leadId;
  logger.info('Form submitted', { leadId });
  notifySubscribers();
}

// ============================================
// Compliance State
// ============================================

export function setAgeVerified(): void {
  state.compliance.ageVerified = true;
  state.compliance.ageVerifiedAt = Date.now();
  state.ui.showAgeGate = false;
  logger.info('Age verified');
  notifySubscribers();
}

export function setGDPRConsent(consent: GDPRConsent): void {
  state.compliance.gdprConsent = consent;
  state.compliance.gdprConsentAt = Date.now();
  state.ui.showGDPRModal = false;
  logger.info('GDPR consent set', { consented: consent.consented });
  notifySubscribers();
}

export function showAgeGate(): void {
  state.ui.showAgeGate = true;
  notifySubscribers();
}

export function showGDPRModal(): void {
  state.ui.showGDPRModal = true;
  notifySubscribers();
}

// ============================================
// Session State
// ============================================

export function setUTMParams(params: UTMParams): void {
  state.session.utmParams = params;
  logger.debug('UTM params set', params);
  notifySubscribers();
}

export function setCustomParams(params: Record<string, string>): void {
  state.session.customParams = params;
  logger.debug('Custom params set', params);
  notifySubscribers();
}

export function setLeadId(leadId: string): void {
  state.session.leadId = leadId;
  notifySubscribers();
}

// ============================================
// UI State
// ============================================

export function setLoading(isLoading: boolean): void {
  state.ui.isLoading = isLoading;
  notifySubscribers();
}

export function setShowModeBanner(show: boolean): void {
  state.ui.showModeBanner = show;
  notifySubscribers();
}

// ============================================
// Persistence
// ============================================

const STORAGE_KEY = 'player2_progress';
const LEAD_ID_STORAGE_KEY = 'player2_lead_id';
const CONSENT_KEY = 'player2_consent';

// Server progress data type
interface ServerProgressData {
  leadID: string;
  sessionId: string;
  funnelID: string;
  funnelVersion: number;
  currentQuestionIndex: number;
  totalQuestions: number;
  answers: Record<string, unknown>;
  userAgent: string;
  referrer: string | null;
  sourceUrl: string;
}

// Import at runtime to avoid circular dependency
let saveProgressToServerFn: ((data: ServerProgressData) => Promise<void>) | null = null;

/**
 * Set the server progress save function (called from main.ts to avoid circular imports)
 */
export function setProgressSaveFunction(fn: (data: ServerProgressData) => Promise<void>): void {
  saveProgressToServerFn = fn;
}

/**
 * Get or create a lead ID for tracking in-progress submissions
 */
export function getOrCreateLeadId(): string {
  // Check if we already have one in state
  if (state.session.leadId) {
    return state.session.leadId;
  }

  // Check sessionStorage
  const storedLeadId = sessionStorage.getItem(LEAD_ID_STORAGE_KEY);
  if (storedLeadId) {
    state.session.leadId = storedLeadId;
    return storedLeadId;
  }

  // Generate new one
  const newLeadId = `lead_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  state.session.leadId = newLeadId;
  sessionStorage.setItem(LEAD_ID_STORAGE_KEY, newLeadId);
  logger.info('Generated new lead ID', { leadId: newLeadId });

  return newLeadId;
}

/**
 * Save current progress to sessionStorage AND server
 */
export function saveProgress(): void {
  try {
    const progressData = {
      funnelId: state.funnel?.funnelID,
      quiz: state.quiz,
      session: {
        sessionId: state.session.sessionId,
        leadId: state.session.leadId
      },
      savedAt: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
    logger.debug('Progress saved to sessionStorage');

    // Also save to server (fire and forget)
    if (state.funnel && saveProgressToServerFn) {
      const leadId = getOrCreateLeadId();
      const serverData = {
        leadID: leadId,
        sessionId: state.session.sessionId,
        funnelID: state.funnel.funnelID,
        funnelVersion: state.funnel.version,
        currentQuestionIndex: state.quiz.currentQuestionIndex,
        totalQuestions: state.funnel.questions?.length || 0,
        answers: state.quiz.answers,
        userAgent: navigator.userAgent,
        referrer: document.referrer || null,
        sourceUrl: window.location.href
      };
      saveProgressToServerFn(serverData);
    }
  } catch (error) {
    logger.warn('Failed to save progress', error);
  }
}

/**
 * Load saved progress from sessionStorage
 */
export function loadProgress(funnelId: string): boolean {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return false;

    const data = JSON.parse(saved);

    // Only restore if same funnel
    if (data.funnelId !== funnelId) {
      logger.debug('Different funnel, not restoring progress');
      return false;
    }

    // Restore quiz state
    state.quiz = data.quiz;
    state.session.sessionId = data.session.sessionId;
    state.session.leadId = data.session.leadId;

    logger.info('Progress restored', {
      questionIndex: state.quiz.currentQuestionIndex,
      answerCount: Object.keys(state.quiz.answers).length
    });

    notifySubscribers();
    return true;
  } catch (error) {
    logger.warn('Failed to load progress', error);
    return false;
  }
}

/**
 * Clear saved progress
 */
export function clearProgress(): void {
  // Clear sessionStorage
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(LEAD_ID_STORAGE_KEY);

  // Also reset in-memory quiz state
  state.quiz = {
    currentQuestionIndex: 0,
    answers: {},
    isComplete: false,
    startedAt: null,
    completedAt: null
  };
  state.session.leadId = null;

  logger.debug('Progress cleared');
}

/**
 * Save consent to localStorage (persists across sessions)
 */
export function saveConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state.compliance.gdprConsent));
    logger.debug('Consent saved');
  } catch (error) {
    logger.warn('Failed to save consent', error);
  }
}

/**
 * Load saved consent from localStorage
 */
export function loadConsent(): GDPRConsent | null {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return null;

    const consent = JSON.parse(saved) as GDPRConsent;
    state.compliance.gdprConsent = consent;
    logger.debug('Consent loaded', consent);
    return consent;
  } catch (error) {
    logger.warn('Failed to load consent', error);
    return null;
  }
}
